import {
  Box,
  Button,
  Divider,
  Flex,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiCheck, FiChevronDown, FiPlus, FiSearch } from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";
import { type SavedReportDto, SavedReportKindDto, type SavedReportListDto } from "../../autogen/bff-api";
import { urls } from "../../urls";
import { personName } from "../components/communication/communicationUtils";
import { savedReportListAllLabel, savedReportListPath } from "./savedReportLists";

interface Props {
  list: SavedReportListDto;
  reports: SavedReportDto[];
  isLoading: boolean;
  failedToLoad: boolean;
  onRetry: () => void;
  openReportId: string | null;
  onOpen: (report: SavedReportDto) => void;
  onOpenAll: () => void;
  onSaveCurrentView: () => void;
}

export const ReportsMenu = ({
  list,
  reports,
  isLoading,
  failedToLoad,
  onRetry,
  openReportId,
  onOpen,
  onOpenAll,
  onSaveCurrentView,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isOpen, onOpen: openMenu, onClose } = useDisclosure();
  const [search, setSearch] = useState("");
  const showsWholeList = openReportId === null && pathname === savedReportListPath(list);

  const matching = reports
    .filter((report) => report.name.toLowerCase().includes(search.trim().toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name, "nb"));
  const firm = matching.filter((report) => report.kind === SavedReportKindDto.Firm);
  const mine = matching.filter((report) => report.kind === SavedReportKindDto.Own);
  const shared = matching.filter((report) => report.kind === SavedReportKindDto.Shared);

  const choose = (action: () => void) => {
    onClose();
    setSearch("");
    action();
  };

  const row = (key: string, label: string, selected: boolean, onClick: () => void, detail?: string) => (
    <Button
      key={key}
      variant="ghost"
      size="sm"
      justifyContent="flex-start"
      fontWeight={selected ? "semibold" : "normal"}
      bg={selected ? "smPrimarySoft" : undefined}
      aria-current={selected ? "true" : undefined}
      onClick={() => choose(onClick)}
      h="auto"
      py="1.5"
      px="2"
      whiteSpace="normal"
      textAlign="left"
    >
      <Flex align="flex-start" gap="2">
        <Box w="4" flexShrink={0} pt="0.5">
          {selected && <Icon as={FiCheck} color="smPrimary" aria-hidden />}
        </Box>
        <Stack spacing="0" align="flex-start">
          <Text as="span">{label}</Text>
          {detail && (
            <Text as="span" fontSize="xs" color="smMuted">
              {detail}
            </Text>
          )}
        </Stack>
      </Flex>
    </Button>
  );

  const group = (heading: string, items: SavedReportDto[]) =>
    items.length > 0 && (
      <Box>
        <Text px="3" pt="2" pb="1" fontSize="xs" fontWeight="bold" textTransform="uppercase" color="smMuted">
          {heading}
        </Text>
        <Stack spacing="0">
          {items.map((report) =>
            row(
              report.id,
              report.name,
              report.id === openReportId,
              () => onOpen(report),
              report.sharedBy ? (t("Shared by {{name}}", { name: personName(report.sharedBy) }) as string) : undefined,
            ),
          )}
        </Stack>
      </Box>
    );

  const reportsBody = isLoading ? (
    <Flex px="3" py="2" align="center" gap="2">
      <Spinner size="xs" />
      <Text fontSize="sm" color="smMuted">
        {t("Loading the reports …")}
      </Text>
    </Flex>
  ) : failedToLoad ? (
    <Flex px="3" py="2" align="center" gap="2" wrap="wrap">
      <Text fontSize="sm" color="smError">
        {t("The reports could not be loaded.")}
      </Text>
      <Button size="sm" variant="link" colorScheme="teal" onClick={onRetry}>
        {t("Try again")}
      </Button>
    </Flex>
  ) : (
    <>
      {group(t("Firm reports"), firm)}
      {group(t("My reports"), mine)}
      {group(t("Shared with me"), shared)}
      {reports.length === 0 && (
        <Text px="3" py="2" fontSize="sm" color="smMuted">
          {t("You have no saved reports for this list yet.")}
        </Text>
      )}
      {reports.length > 0 && matching.length === 0 && (
        <Text px="3" py="2" fontSize="sm" color="smMuted">
          {t("No report matches the search.")}
        </Text>
      )}
    </>
  );

  return (
    <Popover isOpen={isOpen} onOpen={openMenu} onClose={onClose} placement="bottom-end" isLazy>
      <PopoverTrigger>
        <Button size="sm" variant="outline" rightIcon={<Icon as={FiChevronDown} />}>
          {t("Reports")}
        </Button>
      </PopoverTrigger>
      <PopoverContent w="sm" maxW="calc(100vw - 32px)">
        <PopoverBody p="2">
          <Stack spacing="1">
            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none">
                <Icon as={FiSearch} color="smMuted" />
              </InputLeftElement>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("Search the reports") as string}
                aria-label={t("Search the reports") as string}
                autoFocus
              />
            </InputGroup>
            {row("all", savedReportListAllLabel(t, list), showsWholeList, onOpenAll)}
            <Box maxH="60vh" overflowY="auto">
              {reportsBody}
            </Box>
            <Divider />
            <Flex px="2" py="1" gap="4" wrap="wrap" justify="space-between">
              <Button
                size="sm"
                variant="link"
                colorScheme="teal"
                leftIcon={<Icon as={FiPlus} />}
                onClick={() => choose(onSaveCurrentView)}
              >
                {t("Save the current view")}
              </Button>
              <Button
                size="sm"
                variant="link"
                colorScheme="teal"
                onClick={() => choose(() => navigate(urls.app.contracts.reports))}
              >
                {t("Manage in My reports")}
              </Button>
            </Flex>
          </Stack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
